import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { BarChart3, MessageCircle, Clock, Users, Activity } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BotMetrics = ({ botId }) => {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMetrics = useCallback(async () => {
    try {
      const params = botId ? `?bot_id=${botId}` : '';
      const { data } = await axios.get(`${BACKEND_URL}/api/bot/metrics${params}`, { withCredentials: true });
      setMetrics(data);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  }, [botId]);

  useEffect(() => { fetchMetrics(); }, [fetchMetrics]);

  const formatHour = (h) => {
    if (h === 0) return '12 AM';
    if (h < 12) return `${h} AM`;
    if (h === 12) return '12 PM';
    return `${h - 12} PM`;
  };

  if (loading) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="bot-metrics-loading">
        {[0,1,2,3].map(i => (
          <div key={i} className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-2/3 mb-3" />
            <div className="h-8 bg-gray-200 rounded w-1/2" />
          </div>
        ))}
      </div>
    );
  }

  if (!metrics) return null;

  const cards = [
    {
      id: 'messages-today',
      label: 'Mensajes Hoy',
      value: metrics.messages_today,
      sub: `${metrics.messages_month} este mes`,
      icon: MessageCircle,
      color: 'indigo',
    },
    {
      id: 'avg-response',
      label: 'Tiempo de Respuesta',
      value: metrics.avg_response_time_seconds ? `${metrics.avg_response_time_seconds.toFixed(1)}s` : '--',
      sub: 'Promedio',
      icon: Clock,
      color: 'emerald',
    },
    {
      id: 'unique-conversations',
      label: 'Conversaciones',
      value: metrics.unique_conversations,
      sub: 'Contactos unicos',
      icon: Users,
      color: 'purple',
    },
    {
      id: 'active-hours',
      label: 'Horas Mas Activas',
      value: metrics.active_hours.length > 0 ? formatHour(metrics.active_hours[0].hour) : '--',
      sub: metrics.active_hours.length > 1
        ? `Tambien ${metrics.active_hours.slice(1).map(h => formatHour(h.hour)).join(', ')}`
        : 'Sin datos suficientes',
      icon: Activity,
      color: 'amber',
    },
  ];

  const colorMap = {
    indigo: { bg: 'bg-indigo-50', icon: 'text-indigo-600', ring: 'bg-indigo-100' },
    emerald: { bg: 'bg-emerald-50', icon: 'text-emerald-600', ring: 'bg-emerald-100' },
    purple: { bg: 'bg-purple-50', icon: 'text-purple-600', ring: 'bg-purple-100' },
    amber: { bg: 'bg-amber-50', icon: 'text-amber-600', ring: 'bg-amber-100' },
  };

  return (
    <div data-testid="bot-metrics-section">
      <div className="flex items-center gap-2.5 mb-4">
        <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
          <BarChart3 className="w-4 h-4 text-indigo-600" />
        </div>
        <h3 className="text-lg font-semibold text-gray-950">Rendimiento del Bot</h3>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map(card => {
          const c = colorMap[card.color];
          const Icon = card.icon;
          return (
            <div key={card.id} className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow"
              data-testid={`metric-${card.id}`}>
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm text-gray-500 font-medium">{card.label}</span>
                <div className={`w-8 h-8 ${c.ring} rounded-lg flex items-center justify-center`}>
                  <Icon className={`w-4 h-4 ${c.icon}`} />
                </div>
              </div>
              <div className="text-2xl font-bold text-gray-950 mb-1">{card.value}</div>
              <p className="text-xs text-gray-500">{card.sub}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default BotMetrics;
